import { fetcher } from '../../lib/api-fetcher';
import type {
  LeadStatus,
  LeadItem,
  ProductInterestItem,
  CrmActivityItem,
  CrmConsentItem,
  Customer360View,
  PaginatedResponse,
  UpdateLeadDto,
} from './types';

const BASE = '/staff/v1/crm';

// --- Leads ---
export function listLeads(params?: {
  status?: LeadStatus;
  ownerId?: string;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<LeadItem>> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.ownerId) qs.set('ownerId', params.ownerId);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));

  const queryStr = qs.toString();
  return fetcher.get<PaginatedResponse<LeadItem>>(`${BASE}/leads${queryStr ? `?${queryStr}` : ''}`);
}

export function getLead(id: string): Promise<LeadItem> {
  return fetcher.get<LeadItem>(`${BASE}/leads/${id}`);
}

export function createLead(body: {
  partyId?: string;
  prospect?: {
    name: string;
    mobile: string;
    email?: string;
    gender?: 'M' | 'F' | 'OTHER';
    dob?: string;
  };
  source: string;
  ownerId?: string;
  productInterest?: {
    productInstanceId?: string;
    productCategory?: string;
    expectedValuePaise?: number;
  };
  initialNote?: string;
  followUpDate?: string;
  contacts?: Array<{
    type: 'PHONE' | 'EMAIL';
    label?: string;
    value: string;
    isPrimary?: boolean;
    isWhatsapp?: boolean;
  }>;
  addresses?: Array<{
    addressType?: string;
    label?: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    district?: string;
    stateCode: string;
    pincode: string;
    isPrimary?: boolean;
  }>;
}): Promise<LeadItem> {
  return fetcher.post<LeadItem>(`${BASE}/leads`, body);
}

export function startLeadProgress(id: string): Promise<LeadItem> {
  return fetcher.post<LeadItem>(`${BASE}/leads/${id}/start-progress`, {});
}

export function updateLead(id: string, body: UpdateLeadDto): Promise<LeadItem> {
  return fetcher.patch<LeadItem>(`${BASE}/leads/${id}`, body);
}

export function deleteLead(id: string, reason?: string): Promise<{ success: boolean }> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  return fetcher.delete<{ success: boolean }>(`${BASE}/leads/${id}${qs}`);
}

export function qualifyLead(id: string, body: { qualification: Record<string, unknown> }): Promise<LeadItem> {
  return fetcher.post<LeadItem>(`${BASE}/leads/${id}/qualify`, body);
}

export function disqualifyLead(id: string, body: { reason: string }): Promise<LeadItem> {
  return fetcher.post<LeadItem>(`${BASE}/leads/${id}/disqualify`, body);
}

// --- Product Interests ---
export function listProductInterests(params?: {
  partyId?: string;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<ProductInterestItem>> {
  const qs = new URLSearchParams();
  if (params?.partyId) qs.set('partyId', params.partyId);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));

  const queryStr = qs.toString();
  return fetcher.get<PaginatedResponse<ProductInterestItem>>(`${BASE}/product-interests${queryStr ? `?${queryStr}` : ''}`);
}

export function getProductInterest(id: string): Promise<ProductInterestItem> {
  return fetcher.get<ProductInterestItem>(`${BASE}/product-interests/${id}`);
}

export function createProductInterest(body: {
  partyId: string;
  leadId?: string;
  productInstanceId: string;
  source: string;
  ownerId?: string;
  priority?: string;
  expectedValuePaise?: number;
}): Promise<ProductInterestItem> {
  return fetcher.post<ProductInterestItem>(`${BASE}/product-interests`, body);
}

export function deferProductInterest(id: string, body: { reason?: string }): Promise<ProductInterestItem> {
  return fetcher.post<ProductInterestItem>(`${BASE}/product-interests/${id}/defer`, body);
}

export function reactivateProductInterest(id: string): Promise<ProductInterestItem> {
  return fetcher.post<ProductInterestItem>(`${BASE}/product-interests/${id}/reactivate`, {});
}

export function closeProductInterest(id: string, body: { reason: string }): Promise<ProductInterestItem> {
  return fetcher.post<ProductInterestItem>(`${BASE}/product-interests/${id}/close`, body);
}

// --- Activities ---
export function listActivities(partyId: string, params?: { page?: number; limit?: number }): Promise<PaginatedResponse<CrmActivityItem>> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));

  const queryStr = qs.toString();
  return fetcher.get<PaginatedResponse<CrmActivityItem>>(`${BASE}/parties/${partyId}/activities${queryStr ? `?${queryStr}` : ''}`);
}

export function logActivity(body: {
  partyId: string;
  leadId?: string;
  productInterestId?: string;
  customerRelationshipId?: string;
  type: string;
  direction?: 'INBOUND' | 'OUTBOUND';
  ownerId?: string;
  scheduledAt?: string;
  completedAt?: string;
  outcome?: string;
  notes?: string;
}): Promise<CrmActivityItem> {
  return fetcher.post<CrmActivityItem>(`${BASE}/activities`, body);
}

// --- Consents ---
export function listConsents(partyId: string): Promise<PaginatedResponse<CrmConsentItem>> {
  return fetcher.get<PaginatedResponse<CrmConsentItem>>(`${BASE}/parties/${partyId}/consents`);
}

export function captureConsent(body: {
  partyId: string;
  purpose: string;
  scope: string;
  source: string;
  expiresAt?: string;
  evidenceReference?: string;
}): Promise<CrmConsentItem> {
  return fetcher.post<CrmConsentItem>(`${BASE}/consents`, body);
}

export function withdrawConsent(id: string): Promise<CrmConsentItem> {
  return fetcher.post<CrmConsentItem>(`${BASE}/consents/${id}/withdraw`, {});
}

// --- Customer 360 ---
export function getCustomer360(partyId: string): Promise<Customer360View> {
  return fetcher.get<Customer360View>(`${BASE}/parties/${partyId}/360`);
}
